import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['Braids', 'Locs', 'Twists', 'Crochet', 'Maintenance', 'Other'];

const emptyService = {
  name: '',
  description: '',
  price: '',
  duration: '',
  category: 'Braids'
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState(emptyService);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data } = await axios.get(`${API}/services/all`, { withCredentials: true });
      setServices(data);
    } catch (error) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingService(null);
    setFormData(emptyService);
    setIsDialogOpen(true);
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      price: service.price.toString(),
      duration: service.duration.toString(),
      category: service.category
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      duration: parseInt(formData.duration)
    };

    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, payload, { withCredentials: true });
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/services`, payload, { withCredentials: true });
        toast.success('Service created');
      }
      fetchServices();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this service?')) return;
    try {
      await axios.delete(`${API}/services/${id}`, { withCredentials: true });
      toast.success('Service deleted');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const toggleActive = async (id) => {
    try {
      const { data } = await axios.put(`${API}/services/${id}/toggle-active`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchServices();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  const groupedServices = services.reduce((acc, service) => {
    const cat = service.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(service);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9C6A48]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="services-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-light text-[#2B2623]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Services
          </h1>
          <p className="text-[#756A63] mt-1">Manage your service offerings and pricing</p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-[#9C6A48] hover:bg-[#8B5A33] text-white"
          data-testid="add-service-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Service
        </Button>
      </div>

      {services.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E3DACD] p-8 text-center">
          <p className="text-[#756A63]">No services yet. Add your first service!</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedServices).map(([category, categoryServices]) => (
            <div key={category}>
              <h2 className="text-xl font-medium text-[#9C6A48] mb-4">{category}</h2>
              <div className="bg-white rounded-xl border border-[#E3DACD] overflow-hidden">
                <div className="divide-y divide-[#E3DACD]">
                  {categoryServices.map((service) => (
                    <div
                      key={service.id}
                      className="p-4 hover:bg-[#FAF7F2] transition-colors"
                      data-testid={`service-row-${service.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-[#2B2623]">{service.name}</h3>
                            {!service.active && (
                              <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>
                            )}
                          </div>
                          <p className="text-sm text-[#756A63] mt-1">{service.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-[#756A63]">
                            <span>{service.duration} mins</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-xl font-light text-[#9C6A48]">${service.price}</span>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleActive(service.id)}
                              title={service.active ? 'Deactivate' : 'Activate'}
                              data-testid={`toggle-active-btn-${service.id}`}
                            >
                              {service.active ? (
                                <Eye className="w-4 h-4 text-green-600" />
                              ) : (
                                <EyeOff className="w-4 h-4 text-[#756A63]" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(service)}
                              data-testid={`edit-service-btn-${service.id}`}
                            >
                              <Pencil className="w-4 h-4 text-[#756A63]" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteService(service.id)}
                              data-testid={`delete-service-btn-${service.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Service Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#2B2623] mb-2 block">Service Name *</label>
              <Input
                data-testid="service-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Knotless Braids"
                required
                className="border-[#E3DACD]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2B2623] mb-2 block">Description *</label>
              <Textarea
                data-testid="service-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the service..."
                required
                className="border-[#E3DACD]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Price ($) *</label>
                <Input
                  data-testid="service-price-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="0.00"
                  required
                  className="border-[#E3DACD]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Duration (mins) *</label>
                <Input
                  data-testid="service-duration-input"
                  type="number"
                  min="0"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="60"
                  required
                  className="border-[#E3DACD]"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#2B2623] mb-2 block">Category *</label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger data-testid="service-category-select" className="border-[#E3DACD]">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-[#E3DACD]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#9C6A48] hover:bg-[#8B5A33] text-white"
                data-testid="save-service-btn"
              >
                {editingService ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
